import { useCallback, useMemo, useRef, useState } from "react";
import type { GroupQueryState } from "#app/lib/hooks/use-group-query";

/** First-page window before the table's first `fetchPage` call — the
 *  DataTable's default page size. Later pages use the size the table asks for. */
const INITIAL_PAGE = { offset: 0, limit: 50 };

/**
 * Local twin of `useGroupQuery`: pages a fully-materialized in-memory dataset
 * through the same `GroupQueryState` contract the query providers report, so a
 * table over local rows wires up exactly like a table over a paged query. The
 * DataTable's windowed scroll calls `fetchPage(offset, limit)` and the pager
 * answers from `rows` synchronously.
 *
 * `contextKey` is the dataset's semantic identity — serialize whatever changes
 * the MEANING of `rows` (filters, sort, search, the owning entity). Flipping it
 * resets the window to the top, exactly like a query provider's variables
 * change. A mere identity refresh of `rows` (an Apollo cache broadcast) keeps
 * the window in place.
 */
export function useLocalGroupPages<TData>(
  rows: TData[],
  contextKey: string,
): GroupQueryState<TData> {
  const [page, setPage] = useState(INITIAL_PAGE);

  // Reset to the top when the dataset changes meaning — during render, so this
  // render's slice already reads the new offset (mirrors `useGroupQuery`).
  const prevContextKeyRef = useRef(contextKey);
  if (prevContextKeyRef.current !== contextKey) {
    prevContextKeyRef.current = contextKey;
    if (page.offset !== INITIAL_PAGE.offset || page.limit !== INITIAL_PAGE.limit) {
      setPage(INITIAL_PAGE);
    }
  }

  const fetchPage = useCallback((offset: number, limit: number) => {
    setPage((prev) => (prev.offset === offset && prev.limit === limit ? prev : { offset, limit }));
  }, []);

  const data = useMemo(
    () => rows.slice(page.offset, page.offset + page.limit),
    [rows, page],
  );

  return useMemo(
    () => ({
      data,
      total: rows.length,
      loading: false,
      dataOffset: page.offset,
      fetchPage,
      variablesKey: contextKey,
    }),
    [data, rows.length, page.offset, fetchPage, contextKey],
  );
}
